import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { searchEmployees } from '../api/minutesApi'
import type { EmployeePick } from '../types'

/** En az bu kadar karakterde arama tetiklenir (boş/çok kısa sorgu sunucuya gitmez). */
const MIN_CHARS = 2

/** Arama ucu + ayrı önbellek anahtarı (tutanak ucu yetki ister, Ajanda'nınki istemez). */
export interface EmployeeSearchSource {
  fetch: (q: string) => Promise<EmployeePick[]>
  cacheKey: string
}

/** Varsayılan: tutanak araması. Tembel çağrı — testlerin modül mock'u geçerli kalsın. */
const MINUTES_SOURCE: EmployeeSearchSource = {
  fetch: (q) => searchEmployees(q),
  cacheKey: 'minutes-employee-search',
}

/**
 * Katılımcı/atanan kişi araması (039). Sonuçlar **yerel state**'te tutulur ve her aramada **yeni referanslı**
 * dizi yazılır. Neden: PrimeReact AutoComplete panelini yalnız `suggestions` referansı değiştiğinde açar;
 * React Query verisini doğrudan bağlarsak aynı terim (cache) tekrar arandığında referans değişmez ve panel
 * yeniden açılmaz. Önbellek yine korunur (`queryClient.fetchQuery` + `staleTime`). Yarış koruması: yalnız en
 * son çağrının sonucu uygulanır (seq guard). Debounce + min uzunluk AutoComplete `delay`/`minLength` ile.
 */
export function useEmployeeSearch(source: EmployeeSearchSource = MINUTES_SOURCE) {
  const qc = useQueryClient()
  const [suggestions, setSuggestions] = useState<EmployeePick[]>([])
  const [loading, setLoading] = useState(false)
  const seqRef = useRef(0)

  const search = async (raw: string) => {
    const q = (raw ?? '').trim()
    const seq = ++seqRef.current
    if (q.length < MIN_CHARS) {
      setSuggestions([]) // taze boş → panel kapanır
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const data = await qc.fetchQuery({
        queryKey: [source.cacheKey, q],
        queryFn: () => source.fetch(q),
        staleTime: 60_000,
      })
      if (seq === seqRef.current) setSuggestions([...data]) // HER seferinde taze referans
    } catch {
      if (seq === seqRef.current) setSuggestions([])
    } finally {
      if (seq === seqRef.current) setLoading(false)
    }
  }

  return { suggestions, search, loading }
}
